import { Link } from 'react-router-dom';
import { 
  Phone, 
  ArrowRight, 
  Shield, 
  Award, 
  Clock, 
  CheckCircle2, 
  Star,
  Building2,
  Home as HomeIcon,
  HardHat,
  SprayCan,
  Droplets,
  Siren
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { GoogleReviews } from '../components/GoogleReviews';
import { useLanguage } from '../context/LanguageContext';
import { useScrollReveal } from '../hooks/useScrollReveal';

const HERO_IMAGE = "https://images.unsplash.com/photo-1597665863042-47e00964d899?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBicmlnaHQlMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBjbGVhbnxlbnwwfHx8fDE3NzAxNzA0MzV8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLanguage();
  const scrollRef = useScrollReveal();

  const services = [
    {
      icon: Building2,
      title: t('services.commercial'),
      description: t('services.commercialDesc'),
      color: 'blue'
    },
    {
      icon: HomeIcon,
      title: t('services.residential'),
      description: t('services.residentialDesc'),
      color: 'green'
    },
    {
      icon: HardHat,
      title: t('services.postConstruction'),
      description: t('services.postConstructionDesc'),
      color: 'blue'
    },
    {
      icon: SprayCan,
      title: t('services.moveInOut'),
      description: t('services.moveInOutDesc'),
      color: 'green'
    },
    {
      icon: Droplets,
      title: t('services.powerWashing'),
      description: t('services.powerWashingDesc'),
      color: 'blue'
    },
    {
      icon: Siren,
      title: t('services.emergency'),
      description: t('services.emergencyDesc'),
      color: 'red'
    }
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: t('whyChoose.licensed'),
      description: t('whyChoose.licensedDesc')
    },
    {
      icon: Award,
      title: t('whyChoose.professional'),
      description: t('whyChoose.professionalDesc')
    },
    {
      icon: CheckCircle2,
      title: t('whyChoose.attention'),
      description: t('whyChoose.attentionDesc')
    },
    {
      icon: Clock,
      title: t('whyChoose.flexible'),
      description: t('whyChoose.flexibleDesc')
    },
    {
      icon: Star,
      title: t('whyChoose.satisfaction'),
      description: t('whyChoose.satisfactionDesc')
    }
  ];

  return (
    <div data-testid="home-page" ref={scrollRef}>
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden watermark-section" data-testid="hero-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-10 sm:py-12 md:py-20 lg:py-24">
          <div className="max-w-3xl mx-auto text-center">
            {/* Hero Content */}
            <div className="space-y-5 sm:space-y-6 md:space-y-8 animate-fadeInUp content-centered">
              {/* Google Reviews Badge - Larger on mobile */}
              <div className="inline-flex items-center gap-2.5 bg-[#FFF8E1] border border-[#FFD54F] rounded-full px-5 py-2.5 sm:px-5 sm:py-2 shadow-sm" data-testid="reviews-badge">
                <Star size={20} className="fill-[#FFD700] text-[#FFD700] sm:w-[14px] sm:h-[14px]" />
                <span className="text-base sm:text-sm font-bold text-[#5D4037] tracking-tight">{t('hero.badge')}</span>
              </div>
              {/* Hero Headline */}
              <h1 className="text-[2.5rem] leading-[1.1] sm:text-5xl lg:text-6xl font-bold" style={{ fontFamily: 'Poppins, sans-serif' }}>
                <span className="text-[#1E88E5]">Gomez</span>{' '}
                <span className="text-[#4CAF50]">Cleaning</span>{' '}
                <span className="text-[#0F172A]">Service LLC</span>
              </h1>
              <p className="text-base sm:text-lg text-[#334155] max-w-xl mx-auto leading-relaxed">
                Serving Los Angeles County and San Fernando Valley with reliable, top-quality cleaning solutions.
              </p>
              <div className="text-center pt-1">
                <Link to="/contact">
                  <Button className="btn-primary text-base px-8 py-5 sm:py-4" data-testid="hero-quote-btn">
                    {t('hero.requestQuote')}
                    <ArrowRight size={18} />
                  </Button>
                </Link>
              </div>
              <div className="info-centered pt-2">
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-[#4CAF50]" />
                  <span className="text-sm text-[#334155]">{t('hero.licensed')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-[#4CAF50]" />
                  <span className="text-sm text-[#334155]">{t('hero.available')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-surface py-14 sm:py-16 md:py-24" data-testid="about-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center reveal">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('about.title')} <span className="text-[#1E88E5]">{t('about.titleHighlight')}</span>
            </h2>
            <p className="text-base sm:text-lg text-[#334155] leading-relaxed">
              {t('about.description')}
            </p>
            <Link to="/about" className="inline-block mt-6 sm:mt-8">
              <Button className="btn-outline" data-testid="learn-more-btn">
                {t('about.learnMore')}
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-14 sm:py-16 md:py-24 bg-white" data-testid="services-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10 sm:mb-12 md:mb-16 reveal content-centered">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('services.title')} <span className="text-[#4CAF50]">{t('services.titleHighlight')}</span>
            </h2>
            <p className="text-base sm:text-lg text-[#334155] max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className={`service-card p-5 sm:p-6 reveal reveal-delay-${index < 6 ? index + 1 : 6} flex flex-col items-center text-center`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`service-card-${index}`}
              >
                <div className={`icon-container ${service.color === 'blue' ? 'icon-container-blue' : service.color === 'red' ? 'icon-container-red' : 'icon-container-green'} mb-4`}>
                  <service.icon size={28} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {service.title}
                </h3>
                <p className="text-[#334155] text-sm leading-relaxed max-w-[280px] mx-auto">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center mt-10 sm:mt-12">
            <Link to="/services">
              <Button className="btn-primary" data-testid="view-services-btn">
                {t('services.viewAll')}
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-14 sm:py-16 md:py-24 bg-surface watermark-section" data-testid="why-choose-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-10 sm:mb-12 md:mb-16 reveal content-centered">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('whyChoose.title')} <span className="text-[#1E88E5]">{t('whyChoose.titleHighlight')}</span>
            </h2>
            <p className="text-base sm:text-lg text-[#334155] max-w-2xl mx-auto">
              {t('whyChoose.subtitle')}
            </p>
          </div>

          {/* First row - 3 items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
            {whyChooseUs.slice(0, 3).map((item, index) => (
              <div 
                key={item.title}
                className={`bg-white rounded-xl p-5 sm:p-6 border border-[#E2E8F0] reveal reveal-delay-${index + 1} flex flex-col items-center text-center`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`why-choose-${index}`}
              >
                <div className="icon-container icon-container-blue mb-4">
                  <item.icon size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#334155] text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>

          {/* Second row - 2 items centered */}
          <div className="flex flex-col md:flex-row justify-center gap-4 sm:gap-6">
            {whyChooseUs.slice(3).map((item, index) => (
              <div 
                key={item.title}
                className={`bg-white rounded-xl p-5 sm:p-6 border border-[#E2E8F0] reveal reveal-delay-${index + 4} flex flex-col items-center text-center w-full md:w-[calc(33.333%-0.75rem)] md:max-w-[380px]`}
                style={{ animationDelay: `${(index + 3) * 100}ms` }}
                data-testid={`why-choose-${index + 3}`}
              >
                <div className="icon-container icon-container-blue mb-4">
                  <item.icon size={24} />
                </div>
                <h3 className="text-base sm:text-lg font-semibold mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-[#334155] text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews />

      {/* CTA Section */}
      <section className="py-14 sm:py-16 md:py-24 bg-[#E3F2FD] watermark-section" data-testid="cta-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center reveal">
          <div className="content-centered">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('cta.title')} <span className="text-[#1E88E5]">{t('cta.titleHighlight')}</span>
            </h2>
            <p className="text-base sm:text-lg text-[#334155] max-w-2xl mx-auto mb-6 sm:mb-8">
              {t('cta.description')}
            </p>
            <Link to="/contact">
              <Button className="btn-primary text-base px-8 py-5 sm:py-4" data-testid="cta-quote-btn">
                {t('cta.getEstimate')}
                <ArrowRight size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
